import React, { useState } from 'react';
import {
  PlacementWithLogical,
  Menu,
  MenuButton,
  As,
  MenuProps,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import {
  ActionMenuContext,
  ActionMenuContextProps,
} from './action-menu-context';

export interface ActionMenuProps<T = object> extends MenuProps {
  /**
   * The element that triggers the display of the Action Menu.
   * Tip: If you decide to pass your own component to `triggerElement`, it needs to accept a ref so that the `ActionMenu` can be positioned correctly. You can use Chakra's [forwardRef](https://v2.chakra-ui.com/community/recipes/as-prop#option-1-using-forwardref-from-chakra-uireact).
   */
  triggerElement?: As;
  /**
   * The text that will display inside of the trigger element
   */
  triggerText?: string;
  /**
   * Props to pass to the trigger element
   */
  triggerElementProps?: T;
  /**
   * The placement of the Action Menu relative to its reference.
   * @default bottom
   */
  placement?: PlacementWithLogical;
  /**
   * The contents of the Action Menu.
   */
  children: React.ReactNode;
  /** The color mode that the action menu will display.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * Submenu indicator
   */
  isSubmenu?: boolean;
}

/**
 * The `ActionMenu` is an accessible dropdown menu for the common dropdown menu
 * button design pattern. This wrapper component provides context state, and
 * focus management.
 */
export const ActionMenu = ({
  children,
  triggerElement,
  triggerText,
  placement,
  onOpen,
  onClose,
  triggerElementProps = {},
  colorMode: propColorMode,
  isSubmenu = false,
  ...props
}: ActionMenuProps) => {
  const { colorMode } = useColorMode(propColorMode);

  const [actionMenuProviderValue, setActionMenuProviderValue] =
    useState<ActionMenuContextProps>({
      colorMode: colorMode,
      isOpen: props.defaultIsOpen || false,
    });

  const onOpenHandler = () => {
    setActionMenuProviderValue({ colorMode, isOpen: true });
    if (typeof onOpen === 'function') {
      onOpen();
    }
  };

  const onCloseHandler = () => {
    setActionMenuProviderValue({ colorMode, isOpen: false });
    if (typeof onClose === 'function') {
      onClose();
    }
  };
  return (
    <ActionMenuContext.Provider value={actionMenuProviderValue}>
      <Menu
        onOpen={onOpenHandler}
        onClose={onCloseHandler}
        {...props}
        placement={placement}
      >
        {!isSubmenu && triggerElement && (
          <MenuButton
            data-testid="action-menu__trigger-button"
            as={triggerElement}
            {...triggerElementProps}
          >
            {triggerText}
          </MenuButton>
        )}
        {children}
      </Menu>
    </ActionMenuContext.Provider>
  );
};
